import type { SDKConfig, SDKCommonParams, GTCommonParams, GTParams } from './types';

// Get SDK common parameters
export const getSDKCommonParams = (): SDKCommonParams => {
    const config = getGumtreeConfig();

    return {
        uid: config.userId, // User ID
        devid: config.deviceId || getCookie('gt_p'), // Device ID (httpOnly)
        platform: getPlatform(), // Platform (Web/Mobile)
        browsetype: getBrowserType(), // Browser type
        islogin: config.islogin ? 1 : 0, // Login status: 0 (not logged in), 1 (logged in)
        localtime: getTimestamp(), // Timestamp
        timezone: getTimezone(), // Timezone
        osver: getOSVersion(), // OS version
    };
};

// Get business common parameters
export const getGTCommonParams = (eventName: string): GTCommonParams => {
    const config = getGumtreeConfig();
    
    return {
        event_name: eventName,
        content_group: config.content_group,
        content_type: config.content_type,
        user_pseudo_id: getCookie('_ga'), // GA ID (non-httpOnly)
        session_id: config.sessionId || getCookie('gt_s'), // Session ID (httpOnly)
    };
};

// Get complete GT event parameters
export const getGTParams = (eventName = '', params = {}): GTParams => {
    const gtCommonParams = getGTCommonParams(eventName);
    return {
        ...gtCommonParams,
        ...params,
    };
};

export const serializeString = (data: any, separator: string) => {
    return Object.entries(data)
        .map(([key, value]) => {
            const finalValue = (value === undefined || value === null) ? '' : value;
            return `${key}=${finalValue}`;
        })
        .join(separator);
};

export const getGumtreeConfig = (): Partial<SDKConfig> => {
    if (typeof window !== 'undefined' && window.__GUMTREE_ANALYTICS_CONFIG__)
        return window.__GUMTREE_ANALYTICS_CONFIG__;
    return {};
};

export const getGA4Data = (): any => {
    if (typeof window === 'undefined') return null;
    return window.__GA4_DATA_LAYER__;
};

export const getCookie = (name: string): string | undefined => {
    const cookies = document.cookie.split(';').map((cookie) => cookie.trim());
    for (const cookie of cookies) {
        const idx = cookie.indexOf('=');
        const key = cookie.slice(0, idx);
        const value = cookie.slice(idx + 1);
        if (key === name) {
            return decodeURIComponent(value);
        }
    }
    return undefined;
};

export const getPlatform = (): string => {
    return getPlatformFromUserAgent(navigator.userAgent);
};

export const getPlatformFromUserAgent = (userAgent: string): string => {
    const normalizedUserAgent = userAgent.toLowerCase();
    if (/mobile|android|iphone|ipad|ipod/.test(normalizedUserAgent)) {
        return 'Mobile';
    }
    return 'Web';
};

export const getBrowserType = (): string => {
    return getBrowserTypeFromUserAgent(navigator.userAgent);
};

export const getBrowserTypeFromUserAgent = (userAgent: string): string => {
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Edg')) return 'Edge';
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Safari')) return 'Safari';
    return 'Unknown';
};

export const getOSVersion = (): string => {
    return getOSVersionFromUserAgent(navigator.userAgent);
};

export const getOSVersionFromUserAgent = (userAgent: string): string => {
    if (/Windows NT/.test(userAgent)) {
        const match = userAgent.match(/Windows NT (\d+\.\d+)/);
        return match ? `Windows ${match[1]}` : 'Windows';
    }
    if (/Mac OS X/.test(userAgent)) {
        const match = userAgent.match(/Mac OS X (\d+[._]\d+)/);
        return match ? `macOS ${match[1].replace(/_/g, '.')}` : 'macOS';
    }
    if (/Android/.test(userAgent)) {
        const match = userAgent.match(/Android (\d+\.\d+)/);
        return match ? `Android ${match[1]}` : 'Android';
    }
    if (/iOS|iPhone|iPad|iPod/.test(userAgent)) {
        const match = userAgent.match(/OS (\d+(_\d+)?)/);
        return match ? `iOS ${match[1].replace(/_/g, '.')}` : 'iOS';
    }
    return 'Unknown';
};

export const getNetworkType = (): string => {
    const connection = (navigator as any).connection;
    if (connection) {
        return connection.effectiveType || 'unknown';
    }
    return 'unknown';
};

export const getUTCTime = (): string => {
    return new Date().toISOString();
};

export const getLocalTime = (): string => {
    function getLocalISOTime() {
        const now = new Date();

        const offset = -now.getTimezoneOffset(); // getTimezoneOffset() returns a negative number
        const offsetHours = Math.floor(offset / 60);
        const offsetMinutes = offset % 60;

        // Generate timezone string, e.g., +08:00
        const offsetStr =
            (offsetHours >= 0 ? '+' : '-') +
            String(Math.abs(offsetHours)).padStart(2, '0') +
            ':' +
            String(Math.abs(offsetMinutes)).padStart(2, '0');

        // Convert UTC time to local time
        const localTime = new Date(now.getTime() + offset * 60 * 1000);
        const localISO = localTime.toISOString().replace('Z', '');

        // Output format: 2024-12-30T18:30:45.123+08:00
        return `${localISO}${offsetStr}`;
    }

    return getLocalISOTime();
};

// Get local timestamp (13-digit millisecond timestamp)
export const getTimestamp = (): number => {
    return Date.now();
};

export const getTimezone = (): string => {
    function getUTCOffset() {
        const offset = -new Date().getTimezoneOffset() / 60;
        return `UTC${offset >= 0 ? '+' : ''}${offset}`;
    }
    return getUTCOffset();
};
